// Custom hooks for content data with Contentful + fallback support
import { useState, useEffect, useCallback } from 'react';
import {
  getVideos,
  getVideosByCategory,
  getVideoBySlug,
  getBlogPosts,
  getBlogBySlug,
  getBooks,
  getBookBySlug,
  getMarketplaceItems,
  getTopics,
  getAwards,
} from '../services/contentAdapter';

/**
 * Hook to fetch all videos
 */
export const useVideos = () => {
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadVideos = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getVideos();
      setVideos(data);
    } catch (err) {
      setError(err.message);
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  return { videos, isLoading, error, refetch: loadVideos };
};

/**
 * Hook to fetch videos by category
 */
export const useVideosByCategory = (category) => {
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadVideos = useCallback(async () => {
    if (!category) {
      setVideos([]);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      const data = await getVideosByCategory(category);
      setVideos(data);
    } catch (err) {
      setError(err.message);
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  }, [category]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  return { videos, isLoading, error, refetch: loadVideos };
};

/**
 * Hook to fetch a single video by slug
 */
export const useVideo = (slug) => {
  const [video, setVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadVideo = useCallback(async () => {
    if (!slug) {
      setVideo(null);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      const data = await getVideoBySlug(slug);
      setVideo(data);
    } catch (err) {
      setError(err.message);
      setVideo(null);
    } finally {
      setIsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadVideo();
  }, [loadVideo]);

  return { video, isLoading, error, refetch: loadVideo };
};

/**
 * Hook to fetch all blog posts
 */
export const useBlogPosts = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPosts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getBlogPosts();
      setPosts(data);
    } catch (err) {
      setError(err.message);
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  return { posts, isLoading, error, refetch: loadPosts };
};

/**
 * Hook to fetch a single blog post by slug
 */
export const useBlogPost = (slug) => {
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPost = useCallback(async () => {
    if (!slug) {
      setPost(null);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      const data = await getBlogBySlug(slug);
      setPost(data);
    } catch (err) {
      setError(err.message);
      setPost(null);
    } finally {
      setIsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  return { post, isLoading, error, refetch: loadPost };
};

/**
 * Hook to fetch all books
 */
export const useBooks = () => {
  const [books, setBooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadBooks = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getBooks();
      setBooks(data);
    } catch (err) {
      setError(err.message);
      setBooks([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  return { books, isLoading, error, refetch: loadBooks };
};

/**
 * Hook to fetch a single book by slug
 */
export const useBook = (slug) => {
  const [book, setBook] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadBook = useCallback(async () => {
    if (!slug) {
      setBook(null);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      const data = await getBookBySlug(slug);
      setBook(data);
    } catch (err) {
      setError(err.message);
      setBook(null);
    } finally {
      setIsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadBook();
  }, [loadBook]);

  return { book, isLoading, error, refetch: loadBook };
};

/**
 * Hook to fetch all marketplace items
 */
export const useMarketplace = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadItems = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getMarketplaceItems();
      setItems(data);
    } catch (err) {
      setError(err.message);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  return { items, isLoading, error, refetch: loadItems };
};

/**
 * Hook to fetch all topics
 */
export const useTopics = () => {
  const [topics, setTopics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTopics = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getTopics();
      setTopics(data);
    } catch (err) {
      setError(err.message);
      setTopics([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTopics();
  }, [loadTopics]);

  return { topics, isLoading, error, refetch: loadTopics };
};

/**
 * Hook to fetch all awards
 */
export const useAwards = () => {
  const [awards, setAwards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAwards = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAwards();
      setAwards(data);
    } catch (err) {
      setError(err.message);
      setAwards([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAwards();
  }, [loadAwards]);

  return { awards, isLoading, error, refetch: loadAwards };
};
